/**
 * @returns {Object} PTO request methods (bound to `this` of the composed apiService)
 */
export function createPtoRequestMethods() {
  return {
    /**
     * Create a new PTO request
     * @param {Object} data
     * @param {string} data.start_date - YYYY-MM-DD
     * @param {string} data.end_date - YYYY-MM-DD
     * @param {string} [data.start_time] - HH:MM for partial day
     * @param {string} [data.end_time] - HH:MM for partial day
     * @param {string} data.reason
     * @param {string} [data.notes]
     * @returns {Promise<import('@prism-ehr/shared/types/api/ptoRequest').PtoRequestResponse>}
     */
    createPtoRequest(data) {
      return this.request('/pto-request', { method: 'POST', body: JSON.stringify(data) });
    },

    /**
     * List PTO requests with filters
     * @param {Object} [filters]
     * @param {string} [filters.user_id]
     * @param {string} [filters.status]
     * @param {string} [filters.start_date]
     * @param {string} [filters.end_date]
     * @param {number} [filters.page]
     * @param {number} [filters.per_page]
     * @returns {Promise<import('@prism-ehr/shared/types/api/ptoRequest').PtoRequestListResponse>}
     */
    getPtoRequests(filters = {}) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v != null && v !== '') params.set(k, String(v));
      });
      const qs = params.toString();
      return this.request(`/pto-request${qs ? `?${qs}` : ''}`, { method: 'GET' });
    },

    /**
     * Get a single PTO request
     * @param {number} id
     * @returns {Promise<import('@prism-ehr/shared/types/api/ptoRequest').PtoRequestResponse>}
     */
    getPtoRequest(id) {
      return this.request(`/pto-request/${id}`, { method: 'GET' });
    },

    /**
     * Update a PTO request (approve, deny, cancel, edit)
     * @param {number} id
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared/types/api/ptoRequest').PtoRequestResponse>}
     */
    updatePtoRequest(id, data) {
      return this.request(`/pto-request/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
    },

    /**
     * Delete a pending PTO request
     * @param {number} id
     * @returns {Promise<void>}
     */
    deletePtoRequest(id) {
      return this.request(`/pto-request/${id}`, { method: 'DELETE' });
    },

    /**
     * Get PTO balance for a user
     * @param {string} userId
     * @param {number} [year]
     * @param {Object} [preview] - request dates to price: start_date, end_date, start_time, end_time
     *   (response then carries request_minutes / request_days, the figure POST /pto-request stores)
     * @returns {Promise<import('@prism-ehr/shared/types/api/ptoRequest').PtoBalanceResponse>}
     */
    getPtoBalance(userId, year, preview) {
      const params = new URLSearchParams();
      if (userId) params.set('user_id', String(userId));
      if (year) params.set('year', String(year));
      if (preview) {
        Object.entries(preview).forEach(([k, v]) => {
          if (v != null && v !== '') params.set(k, String(v));
        });
      }
      return this.request(`/pto-balance?${params.toString()}`, { method: 'GET' });
    },
  };
}
